import { useCallback, useEffect, useState } from 'react';
import { Acceso } from './acceso/Acceso';
import { Invitacion } from './acceso/Invitacion';
import type { Operaciones, ParDeTokens } from './api/operaciones';
import type { CrearApi } from './cliente/apiCliente';
import { ZonaCliente } from './cliente/ZonaCliente';
import { Backoffice } from './backoffice/Backoffice';
import { crearVueltaAlBackoffice } from './backoffice/vuelta';
import { ProveedorAvisoBreve } from './componentes/AvisoBreve';
import { useAvisoBreve } from './componentes/avisoBreve';
import { RUTA_DE_PANTALLA } from './logica/acceso';
import { recordarCodigoPartner } from './logica/altaNegocio';
import { useSesionPanel } from './logica/sesionPanel';
import { PanelNegocio } from './marco/PanelNegocio';
import { PortalPartner } from './partners/PortalPartner';
import { SolicitudPartner } from './partners/SolicitudPartner';
import { RUTAS_ACCESO, RUTA_BACKOFFICE, RUTA_INVITACION, RUTA_PORTAL_PARTNER, RUTA_SOLICITUD_PARTNER, RUTA_TABLET, esRutaCliente } from './rutas';
import { almacenDelNavegador, type AlmacenSesion } from './sesion/sesion';
import { AppTablet } from './tablet/AppTablet';
import './estilo/comun/colors_and_type.css';
import './estilo/comun/base.css';
import './estilo/comun/iconos.css';
import './estilo/panel.css';

/**
 * Panel del negocio (Paso 0 con la exportación de Claude Code, G127, ADR-0050): inicio de sesión y elegir negocio (hoja
 * de acceso roja) y el marco con su menú (0.1 a 0.5). Solo habla con la API v1 a través de `ops`; con
 * `VITE_SIMULACION=1`, esas mismas operaciones responden con datos simulados con la forma del contrato (src/simulacion/).
 */
export interface PropsApp {
    ops: Operaciones;
    almacen: AlmacenSesion;
    simulacion?: boolean;
    /** Ruta inicial al pintar fuera del navegador (pruebas). */
    rutaServidor?: string;
    /** Operaciones con otra credencial (la tablet, 1.13); por defecto, las mismas `ops`. */
    crearOps?: (obtenerToken: () => string | null) => Operaciones;
    /** Cliente de la API de las páginas del cliente, con el token de cliente (G157); nunca la sesión del equipo. */
    crearApiCliente?: CrearApi;
}

export function App(props: PropsApp) {
    return (
        <ProveedorAvisoBreve>
            <Panel {...props} />
        </ProveedorAvisoBreve>
    );
}

function Panel({ ops, almacen, simulacion = false, rutaServidor = '/', crearOps, crearApiCliente }: PropsApp) {
    const s = useSesionPanel(ops, almacen, rutaServidor);
    const avisar = useAvisoBreve();
    const { navegar } = s;
    // El enlace de alta de un partner (1.23): «Crear cuenta» con su código (`?p=`), que se recuerda para «Tu negocio» (1.5).
    const codigoPartner = s.ruta === RUTAS_ACCESO.crearCuenta ? s.busqueda.get('p') : null;
    useEffect(() => {
        if (codigoPartner !== null) {
            recordarCodigoPartner(codigoPartner);
        }
    }, [codigoPartner]);
    // La sesión del backoffice mientras se está dentro de un negocio con un acceso de plataforma (1.20, G137).
    const [vuelta] = useState(() => crearVueltaAlBackoffice(almacenDelNavegador()));
    const { cerrarSesion } = s;
    const cerrarTodo = useCallback(() => {
        vuelta.borrar();
        cerrarSesion();
    }, [vuelta, cerrarSesion]);
    const alAbrirAcceso = useCallback(
        (tokens: ParDeTokens) => {
            const actual = almacen.actual();
            if (actual !== null) {
                vuelta.guardar(actual);
            }
            almacen.guardar(tokens);
            navegar('/', true);
        },
        [almacen, vuelta, navegar],
    );
    // «Salir» de la franja índigo: vuelve a la sesión del backoffice guardada al entrar; sin ella (otra pestaña, se
    // borró), cierra la sesión del acceso y lleva al inicio de sesión (G1130).
    const volverAlBackoffice = useCallback(() => {
        const tokens = vuelta.tomar();
        if (tokens === null) {
            cerrarTodo();
            return true;
        }
        almacen.guardar(tokens);
        navegar(RUTA_BACKOFFICE, true);
        return true;
    }, [almacen, vuelta, navegar, cerrarTodo]);
    const irA = useCallback(
        (p: keyof typeof RUTA_DE_PANTALLA) => {
            navegar(RUTA_DE_PANTALLA[p]);
        },
        [navegar],
    );

    if (s.ruta === RUTA_TABLET || s.ruta.startsWith(`${RUTA_TABLET}/`)) {
        return (
            <div data-pantalla="tablet" data-simulacion={simulacion ? '1' : undefined}>
                <AppTablet crearOps={crearOps ?? (() => ops)} ruta={s.ruta} busqueda={s.busqueda} navegar={navegar} />
            </div>
        );
    }

    if (esRutaCliente(s.ruta)) {
        return (
            <div data-pantalla="cliente" data-simulacion={simulacion ? '1' : undefined}>
                <ZonaCliente ruta={s.ruta} busqueda={s.busqueda} navegar={navegar} crearApi={crearApiCliente} />
            </div>
        );
    }

    if (s.ruta === RUTA_SOLICITUD_PARTNER) {
        return (
            <div data-pantalla="solicitud-partner" data-simulacion={simulacion ? '1' : undefined}>
                <SolicitudPartner ops={ops} navegar={navegar} />
            </div>
        );
    }

    // Portal de partners (1.23): con sesión y sin negocio, con su propio marco (G133).
    if (s.ruta === RUTA_PORTAL_PARTNER && s.sesion !== null && s.sesion.negocio === null && s.pantallaAcceso === null) {
        return (
            <div data-pantalla="portal-partner" data-simulacion={simulacion ? '1' : undefined}>
                <PortalPartner ops={ops} ruta={s.ruta} busqueda={s.busqueda} navegar={navegar} onCerrarSesion={s.cerrarSesion} onSesionCaducada={s.salir} />
            </div>
        );
    }

    // Backoffice de plataforma (1.20 a 1.22): con sesión y sin negocio, con su propio marco (G133).
    if (s.ruta === RUTA_BACKOFFICE && s.sesion !== null && s.sesion.negocio === null && s.pantallaAcceso === null) {
        return (
            <div data-pantalla="backoffice" data-simulacion={simulacion ? '1' : undefined}>
                <Backoffice
                    ops={ops}
                    busqueda={s.busqueda}
                    navegar={navegar}
                    onCerrarSesion={cerrarTodo}
                    onSesionCaducada={s.salir}
                    onFaltanDosPasos={s.faltanLosDosPasos}
                    onAccesoAbierto={alAbrirAcceso}
                />
            </div>
        );
    }

    if (s.ruta === RUTA_INVITACION) {
        return (
            <div data-pantalla="invitacion" data-simulacion={simulacion ? '1' : undefined}>
                <Invitacion
                    ops={ops}
                    token={s.busqueda.get('token')}
                    sesion={s.sesion}
                    onSesion={(t) => {
                        almacen.guardar(t);
                    }}
                    onSalir={() => {
                        almacen.borrar();
                    }}
                    onEntrar={s.elegir}
                    navegar={navegar}
                />
            </div>
        );
    }

    if (s.pantallaAcceso !== null || s.sesion === null || s.sesion.negocio === null) {
        return (
            <div data-pantalla={s.pantallaAcceso ?? 'login'} data-simulacion={simulacion ? '1' : undefined}>
                <Acceso
                    pantalla={s.pantallaAcceso ?? 'login'}
                    ops={ops}
                    tokenNueva={s.busqueda.get('token')}
                    conSesion={s.sesion !== null}
                    onContinuar={() => {
                        navegar('/', true);
                    }}
                    eleccion={s.eleccion}
                    onIr={irA}
                    onSesion={s.alEntrar}
                    onSalir={s.cerrarSesion}
                />
            </div>
        );
    }

    return (
        <div data-pantalla="panel" data-ruta={s.ruta} data-simulacion={simulacion ? '1' : undefined}>
            <PanelNegocio
                key={s.sesion.negocio}
                ops={ops}
                negocio={s.sesion.negocio}
                ruta={s.ruta}
                busqueda={s.busqueda}
                navegar={navegar}
                simulacion={simulacion}
                onCambiarNegocio={(id) => {
                    s.elegir(id).catch(() => {
                        avisar({ tipo: 'error', texto: 'No he podido cambiar de negocio. Prueba de nuevo.' });
                    });
                }}
                onCerrarSesion={cerrarTodo}
                onSesionCaducada={s.salir}
                onFaltanDosPasos={s.faltanLosDosPasos}
                onSalirDelAcceso={volverAlBackoffice}
            />
        </div>
    );
}
